import { type AxiosError, isAxiosError } from 'axios';
import { ZodError } from 'zod';
import { type LogLevel } from './logger.types';

/**
 * Bounded request/response metadata lifted from an AxiosError. Lives as a top-level `api` sibling
 * to `error` so Kibana queries can filter on `api.response.status` etc.
 *
 * The raw `response.data` (the API response body) is deliberately NOT shipped: it is arbitrary and
 * unbounded, which inflates fluent-bit/OpenSearch field cardinality. We keep only the body's
 * `message` field if present.
 */
const extractApiStats = (axiosError: AxiosError) => ({
  request: {
    url: axiosError?.config?.url,
    baseURL: axiosError?.config?.baseURL,
    method: axiosError?.config?.method,
  },
  response: axiosError?.response?.status
    ? {
        status: axiosError?.response?.status,
        statusText: axiosError?.response?.statusText,
        message: (axiosError?.response?.data as { message?: string })?.message,
      }
    : null,
});

/**
 * Ensure important fields are logged while reducing noise.
 *
 * Axios `config` is omitted on purpose: it includes request headers (e.g. Authorization). Safe
 * request metadata lives under `api.request` instead. If you add `config` back to this payload,
 * also redact sensitive headers (e.g. Pino `redact` paths for `error.config.headers.Authorization`).
 */
export const extractAxiosError = (error: AxiosError) => ({
  code: error.code,
  message: error.message,
  name: error.name,
  stack: error.stack,
  status: error.response?.status,
});

/**
 * Bounded ZodError shape. The raw `error.issues` array can carry arbitrarily deep `aggregateErrors`
 * trees and ZodError's default `message` is the JSON-stringified issues blob — both unsafe to ship
 * to log aggregators. Here we keep only the searchable fields per issue and a concise summary
 * message.
 */
export const extractZodError = (error: ZodError) => {
  const issues = error.issues.map((issue) => ({
    path: issue.path.join('.'),
    message: issue.message,
    code: issue.code,
  }));
  const firstIssue = issues[0];
  let summary: string;
  if (firstIssue) {
    summary = `${firstIssue.message} at ${firstIssue.path || '(root)'}`;
    if (issues.length > 1) {
      summary += ` (+${issues.length - 1} more)`;
    }
  } else {
    summary = 'ZodError: no issues';
  }

  return {
    issues,
    message: summary,
    name: 'ZodError',
    stack: error.stack,
  };
};

/**
 * Formats an error for logging. If no error is provided, it will create one here to
 * ensure we have a stack trace.
 */
export const formatError = (
  level: LogLevel,
  message: string,
  error: unknown,
) => {
  if (isAxiosError(error)) {
    return {
      api: extractApiStats(error),
      error: extractAxiosError(error),
      message,
    };
  } else {
    let ensuredError = error;
    if (level === 'error' && !ensuredError) {
      ensuredError = new Error(message);
    }

    return {
      error: ensuredError,
      message,
    };
  }
};
